import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";

import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    InputAdornment,
    IconButton
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [systemDetails, setSystemDetails] = useState({});

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    useEffect(() => {
        setSystemDetails({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
    }, []);

    const handleLogin = async (e) => {
        // Prevent default form submission reload
        e.preventDefault();

        if (!email || !password) {
            return Toast.fire({
                icon: "error",
                title: "Enter Email & Password"
            });
        }

        setLoading(true);

        try {
            await signInWithEmailAndPassword(auth, email, password);
            const user = auth.currentUser;

            if (user) {
                const docRef = doc(db, "authroles", user.uid);
                const docSnap = await getDoc(docRef);
                const userData = docSnap.exists() ? docSnap.data() : {};

                await addDoc(collection(db, "logindetails"), {
                    uid: user.uid,
                    username: userData.name || "Unknown User",
                    systemDetails,
                    createdTime: Timestamp.now()
                });

                Toast.fire({
                    icon: "success",
                    title: "Login Successful"
                });

                window.location.href = "/home";
            }
        } catch (error) {
            console.error(error);
            Toast.fire({
                icon: "error",
                title: "Invalid Credentials"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "#f8fafc" }}>
            <Grid className="row" sx={{ flex: 1 }}>
                
                {/* LEFT SIDE: PREMIUM BRAND HERO SECTION */}
                <Grid
                    item className="col-sm-6"
                    xs={12}
                    md={6}
                    sx={{
                        background: "linear-gradient(135deg, #0f172a 0%, #0d9488 100%)",
                        color: "#fff",
                        display: { xs: "none", md: "flex" },
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 6,
                        position: "relative",
                        overflow: "hidden"
                    }}
                >
                    {/* Futuristic Background Glows */}
                    <Box sx={{
                        position: "absolute",
                        width: 600,
                        height: 600,
                        background: "radial-gradient(circle, rgba(20,184,166,0.15) 0%, rgba(0,0,0,0) 70%)",
                        top: -200,
                        right: -200,
                        borderRadius: "50%"
                    }} />
                    <Box sx={{
                        position: "absolute",
                        width: 500,
                        height: 500,
                        background: "radial-gradient(circle, rgba(14,165,233,0.1) 0%, rgba(0,0,0,0) 70%)",
                        bottom: -150,
                        left: -150,
                        borderRadius: "50%"
                    }} />

                    {/* Branding Content */}
                    <Box sx={{ position: "relative", zIndex: 2, maxWidth: 460, textAlign: "center" }}>
                        <Box 
                            component="img" 
                            src="/logo.webp" 
                            alt="Company Logo"
                            sx={{ 
                                height: 100, 
                                mb: 4, 
                                filter: "drop-shadow(0px 8px 16px rgba(0,0,0,0.2))",
                                transition: "transform 0.3s ease",
                                "&:hover": { transform: "scale(1.05)" }
                            }} 
                        />

                        <Typography sx={{
                            fontSize: { md: 36, lg: 42 },
                            fontWeight: 800,
                            letterSpacing: "-0.5px",
                            lineHeight: 1.2,
                            background: "linear-gradient(to right, #ffffff, #e2e8f0)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent"
                        }}>
                            GIS World Hospitality Services Pvt. Ltd.
                        </Typography>

                        <Typography sx={{
                            mt: 2.5,
                            fontSize: 18,
                            color: "rgba(255, 255, 255, 0.75)",
                            fontWeight: 400,
                            letterSpacing: "0.5px"
                        }}>
                            Welcome to the Attendance Portal
                        </Typography>
                    </Box>

                    {/* Bottom subtle copyright note */}
                    <Typography sx={{ position: "absolute", bottom: 24, fontSize: 13, opacity: 0.4, letterSpacing: 1 }}>
                        © {new Date().getFullYear()} GIS World. All rights reserved.
                    </Typography>
                </Grid>

                {/* RIGHT SIDE: MODERN LOGIN FORM */}
                <Grid
                    item className="col-sm-6"
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        background: { xs: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", md: "#f8fafc" }
                    }}
                >
                    <Paper 
                        elevation={0}
                        component="form"
                        onSubmit={handleLogin}
                        sx={{
                            width: "100%",
                            maxWidth: 420,
                            p: { xs: 4, sm: 6 },
                            borderRadius: 5,
                            bgcolor: "#ffffff",
                            boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.08)",
                            border: "1px solid rgba(226, 232, 240, 0.8)"
                        }}
                    >
                        {/* Mobile Header View */}
                        <Box sx={{ display: { xs: "block", md: "none" }, textAlign: "center", mb: 3 }}>
                            <img src="/logo.webp" height={60} alt="Logo" style={{ marginBottom: 12 }} />
                            <Typography variant="h6" fontWeight="800" color="#0f172a">
                                GIS World
                            </Typography>
                        </Box>

                        <Typography
                            variant="h5"
                            fontWeight={800}
                            color="#0f172a"
                            sx={{ mb: 1, letterSpacing: "-0.5px", textAlign: { xs: "center", md: "left" } }}
                        >
                            Login Portal
                        </Typography>

                        <Typography
                            sx={{ mb: 4, color: "#64748b", fontSize: 15, textAlign: { xs: "center", md: "left" } }}
                        >
                            Enter your credentials to access your dashboard.
                        </Typography>

                        {/* Input Fields */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                variant="outlined"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email sx={{ color: "#94a3b8", fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 3,
                                        "&:hover fieldset": { borderColor: "#0d9488" },
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Password"
                                variant="outlined"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock sx={{ color: "#94a3b8", fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton 
                                                onClick={() => setShowPassword(!showPassword)} 
                                                edge="end"
                                                size="small"
                                            >
                                                {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 3,
                                        "&:hover fieldset": { borderColor: "#0d9488" },
                                    }
                                }}
                            />

                            {/* Action Button */}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    mt: 1.5,
                                    py: 1.6,
                                    fontWeight: "bold",
                                    fontSize: 16,
                                    borderRadius: 3,
                                    textTransform: "none",
                                    background: "linear-gradient(90deg, #0f172a 0%, #1e293b 100%)",
                                    boxShadow: "0 10px 20px -5px rgba(15, 23, 42, 0.3)",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        background: "linear-gradient(90deg, #1e293b 0%, #0f172a 100%)",
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 12px 24px -5px rgba(15, 23, 42, 0.4)",
                                    }
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Sign In"
                                )}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    );
}

export default Login;